import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import Header from './components/Header'
import Footer from './components/Footer'
import BottomNav from './components/BottomNav'
import PersistentRoute from './components/PersistentRoute'
import Login from './pages/Login'
import Onboarding from './pages/Onboarding'
import Dashboard from './pages/Dashboard'
import PremierCompte from './pages/PremierCompte'
import Operations from './pages/Operations'
import Parametres from './pages/Parametres'
import Profil from './pages/Profil'
import Aide from './pages/Aide'
import APropos from './pages/APropos'
import { useAuth } from './hooks/useAuth'
import { usePlatform } from './hooks/usePlatform'
import { ToastProvider } from './components/ToastProvider'
import { ConfirmProvider } from './components/ConfirmProvider'

// Plotly pèse plusieurs Mo : ne le charger qu'à la première visite de Graphiques.
const Graphiques = lazy(() => import('./pages/Graphiques'))

// Référence du design system, servie sur /design. Chargée à la demande
// pour qu'elle ne pèse rien dans le bundle de l'application.
const DesignSystem = lazy(() => import('./pages/DesignSystem'))

function GraphiquesFallback() {
  return (
    <div className="app-page">
      <h1 className="app-page-title">Graphiques</h1>
      <p className="app-page-subtitle">Préparation des graphiques…</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="skeleton h-64" />
        <div className="skeleton h-64" />
      </div>
    </div>
  )
}

function App() {
  const auth = useAuth()
  const platform = usePlatform()

  const isAuthenticated = auth?.isAuthenticated || false
  const onboardingCompleted = auth?.onboardingCompleted || false
  const isAndroid = platform?.isAndroid || false
  const authLoading = auth?.loading ?? false
  const hasToken = !!auth?.token
  const checkingAuth = hasToken && authLoading

  return (
    <ToastProvider>
      <ConfirmProvider>
    <Router>
      <div className="app-shell">
        {checkingAuth && (
          <div className="flex min-h-screen flex-1 items-center justify-center">
            <p className="text-base text-ink-muted" role="status">Chargement…</p>
          </div>
        )}
        {!checkingAuth && isAuthenticated && onboardingCompleted && <Header />}
        {!checkingAuth && (
        <main className={`flex-1 ${isAndroid ? 'pb-20' : ''}`}>
          {isAuthenticated && onboardingCompleted && (
            <>
              <PersistentRoute path="/dashboard">
                {(active) => <Dashboard active={active} />}
              </PersistentRoute>
              <PersistentRoute path="/operations">
                {(active) => <Operations active={active} />}
              </PersistentRoute>
              <PersistentRoute path="/graphiques">
                {(active) => (
                  <Suspense fallback={<GraphiquesFallback />}>
                    <Graphiques active={active} />
                  </Suspense>
                )}
              </PersistentRoute>
              <PersistentRoute path="/parametres">
                {(active) => <Parametres active={active} />}
              </PersistentRoute>
            </>
          )}
          <Routes>
            {/* Hors authentification : permet de juger le rendu sans backend. */}
            <Route
              path="/design"
              element={
                <Suspense fallback={<div className="app-page"><div className="skeleton h-64" /></div>}>
                  <DesignSystem />
                </Suspense>
              }
            />
            <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />

            {isAuthenticated ? (
              <>
                <Route
                  path="/"
                  element={
                    onboardingCompleted ? <Navigate to="/dashboard" replace /> : <Onboarding />
                  }
                />
                {onboardingCompleted ? (
                  <>
                    <Route path="/premier-compte" element={<PremierCompte />} />
                    <Route path="/dashboard" element={null} />
                    <Route path="/operations" element={null} />
                    <Route path="/graphiques" element={null} />
                    <Route path="/parametres" element={null} />
                    <Route path="/profil" element={<Profil />} />
                    <Route path="/aide" element={<Aide />} />
                    <Route path="/apropos" element={<APropos />} />
                  </>
                ) : (
                  <Route path="*" element={<Navigate to="/" replace />} />
                )}
              </>
            ) : (
              <Route path="*" element={<Navigate to="/login" replace />} />
            )}
          </Routes>
        </main>
        )}
        {!checkingAuth && isAuthenticated && onboardingCompleted && (
          isAndroid ? <BottomNav /> : <Footer />
        )}
      </div>
    </Router>
      </ConfirmProvider>
    </ToastProvider>
  )
}

export default App
